/**
 * @fileoverview RecentDealsList component
 * Renders a list of recent deal cards with error handling and empty states
 * Maps through deals array and renders individual RecentDealCard components
 */

import RecentDealCard from '../RecentDealCard/RecentDealCard';
import type { DealsListProps } from '../../types/RecentDealsProps';
import { RecentDealListWrapper, StyledWrapper } from './RecentDealsList.style';

/**
 * RecentDealsList component
 * Handles the rendering of multiple recent deal cards:
 * - Displays error message if errorDetails is provided
 * - Shows "No Deals Found" message if deals array is empty
 * - Maps through deals array and renders a RecentDealCard for each deal
 * - Passes click handlers for card selection and deal opening
 *
 * @param deals - Array of recent deal objects to display
 * @param onOpenDeal - Callback to open a deal in a new tab when title is clicked
 * @param errorDetails - Optional error object to display error state
 * @returns List of deal cards, error message, or empty state message
 */
const RecentDealsList: React.FC<DealsListProps> = ({ deals, onOpenDeal, errorDetails }) => {
    if (errorDetails) {
        return (
            <StyledWrapper>
                <p>Error loading deals: {errorDetails.message}</p>
            </StyledWrapper>
        );
    }
    if (deals.length === 0) {
        return (
            <StyledWrapper>
                <p>No Deals Found</p>
            </StyledWrapper>
        );
    }

    return (
        <RecentDealListWrapper>
            {deals.map((deal) => (
                <RecentDealCard
                    key={deal.dealId}
                    deal={deal}
                    onCardClick={(deal) => deal.onDealSelect(deal.dealId)}
                    onTitleClick={(deal) => onOpenDeal(deal.dealId)}
                />
            ))}
        </RecentDealListWrapper>
    );
};

export default RecentDealsList;
